import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { useContext } from 'react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import { SanityIcon } from 'components/SanityIcon';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { LocaleContext } from 'components/rebrand/Layout';

import { ISanityImgWithAltText } from 'typescriptDefs';

import { NavigationButtons } from './SwiperCarousel';
import { H3, H4, P } from './Typography';

type CarouselCard = {
  content: string | PortableTextBlock | PortableTextBlock[];
  heading?: string | PortableTextBlock | PortableTextBlock[];
  image: ISanityImgWithAltText;
};

type CarouselVariant = 'primary' | 'primaryInverse' | 'tertiary';

interface Props {
  cards: CarouselCard[];
  classNameOverrides?: {
    sectionClassName?: string;
    headingClassName?: string;
    cardClassName?: string;
    navClassName?: string;
  };
  heading: string | PortableTextBlock | PortableTextBlock[];
  headingStyle?: boolean;
  navBelowCarousel?: boolean;
  nextNavigationClass: string;
  prevNavigationClass: string;
  subheading?: string;
  variant?: CarouselVariant;
}

const OverflowCarousel = ({
  cards,
  classNameOverrides,
  heading,
  headingStyle,
  navBelowCarousel = false,
  nextNavigationClass,
  prevNavigationClass,
  subheading,
  variant = 'primary',
}: Props) => {
  const navigationButtons = (
    <NavigationButtons
      className="[&_button]:h-40 [&_button]:w-40"
      buttonVariant={variant}
      nextNavEl={nextNavigationClass}
      prevNavEl={prevNavigationClass}
    />
  );

  return (
    <Section
      className={twMerge(
        clsx('py-64 md:py-96', variant === 'tertiary' && 'bg-grey-50', classNameOverrides?.sectionClassName),
      )}
    >
      <Container className="px-32 xs:px-0">
        <div
          className={twMerge(
            clsx(
              'mb-40 grid md:mb-48 md:grid-cols-[1fr_auto] md:items-end md:justify-between',
              navBelowCarousel && 'flex flex-col items-center',
            ),
          )}
        >
          <div
            className={twMerge(
              'w-full text-primary-400 md:w-2/3 lg:w-[55%] [&_p]:mt-12 [&_p]:text-grey-500',
              navBelowCarousel && 'mx-auto text-center lg:w-[55%] md:[&_p]:mx-auto md:[&_p]:w-[80%]',
              classNameOverrides?.headingClassName,
            )}
          >
            {typeof heading === 'string' ? (
              <H3 headingStyle={headingStyle}>{heading}</H3>
            ) : (
              <CustomPortableText headingStyles={{ h2: { headingStyle }, h3: { headingStyle } }} content={heading} />
            )}
          </div>
          {subheading && (
            <P className="mx-auto mb-40 mt-24 text-center text-md text-grey-300 md:mb-48 md:mt-16 md:w-[40%] md:text-lg">
              {subheading}
            </P>
          )}
          {!navBelowCarousel && <div className="hidden md:block">{navigationButtons}</div>}
        </div>
        <HorizontalOverflowCarousel
          cards={cards}
          nextNavigationClass={nextNavigationClass}
          prevNavigationClass={prevNavigationClass}
          variant={variant}
          cardClassName={classNameOverrides?.cardClassName}
        />
        <div
          className={twMerge(
            clsx(
              'mt-40 block md:hidden',
              navBelowCarousel && 'md:flex md:justify-end',
              classNameOverrides?.navClassName,
            ),
          )}
        >
          {navigationButtons}
        </div>
      </Container>
    </Section>
  );
};

export default OverflowCarousel;

const CarouselCard = ({
  card: { content, heading, image },
  className,
  variant,
}: {
  card: CarouselCard;
  className?: string;
  variant: CarouselVariant;
}) => {
  return (
    <div
      className={twMerge(
        clsx(
          'rounded-16 p-32',
          variant === 'primary' && 'bg-primary-75',
          variant === 'primaryInverse' && 'bg-secondary-75',
          variant === 'tertiary' && 'bg-white',
          className,
        ),
      )}
    >
      <div className="mb-20">
        <SanityIcon icon={image} objectFit="contain" />
      </div>
      <div
        className={twMerge(
          clsx(
            'mb-16 [&_*]:text-3xl [&_*]:font-bold [&_a]:underline [&_a]:underline-offset-8',
            variant === 'primary' && 'text-primary-350',
            variant === 'primaryInverse' && 'text-secondary-500',
            variant === 'tertiary' && 'text-primary-400',
          ),
        )}
      >
        {typeof heading === 'string' ? (
          <H4 headingStyle>{heading}</H4>
        ) : !!heading ? (
          <CustomPortableText
            headingStyles={{ h3: { headingStyle: true }, h4: { headingStyle: true } }}
            content={heading}
          />
        ) : null}
      </div>
      <div
        className={twMerge(
          clsx(
            '[&_h3]:mb-8 [&_h3]:text-3xl [&_h3]:font-bold [&_p]:text-md md:[&_p]:text-md',
            variant === 'primary' && '[&_h3]:text-primary-350',
            variant === 'primaryInverse' && '[&_h3]:text-secondary-500',
          ),
        )}
      >
        {typeof content === 'string' ? (
          <P className="text-md md:text-md">{content}</P>
        ) : (
          <CustomPortableText
            headingStyles={{ h3: { headingStyle: true }, h4: { headingStyle: true } }}
            content={content}
            noCheckmarkList
          />
        )}
      </div>
    </div>
  );
};

const HorizontalOverflowCarousel = ({
  cards,
  cardClassName,
  nextNavigationClass,
  prevNavigationClass,
  variant,
}: {
  cards: CarouselCard[];
  cardClassName?: string;
  nextNavigationClass: string;
  prevNavigationClass: string;
  variant: CarouselVariant;
}) => {
  const locale = useContext(LocaleContext);

  return (
    <>
      <Swiper
        className="!overflow-visible"
        slidesPerView="auto"
        spaceBetween={32}
        modules={[A11y, Keyboard, Navigation, Pagination]}
        slidesOffsetAfter={0}
        centeredSlides={false}
        a11y={{
          prevSlideMessage: {
            en: 'Previous slide',
            fr: 'Diapositive précédente',
          }[locale],
          nextSlideMessage: { en: 'Next slide', fr: 'Diapositive suivante' }[locale],
        }}
        keyboard
        navigation={{ enabled: true, nextEl: `.${nextNavigationClass}`, prevEl: `.${prevNavigationClass}` }}
      >
        {cards.map((card, index) => (
          <SwiperSlide key={index} className={twMerge(clsx('!h-auto !w-fit'))}>
            <CarouselCard
              className={twMerge('h-full w-240 min-[340px]:w-300 md:w-359', cardClassName)}
              card={card}
              variant={variant}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};
